import { useEffect, useMemo, useState } from 'react';
import './DataTable.css';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function DataTable({ columns, rows = [], onRowClick, pageSize = 10 }) {
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [rows, pageSize]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  const pageRows = useMemo(() => {
    const start = page * pageSize;
    return rows.slice(start, start + pageSize);
  }, [page, pageSize, rows]);

  const handlePrevious = () => {
    setPage(prev => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setPage(prev => Math.min(prev + 1, totalPages - 1));
  };

  const handleRowClick = row => {
    if (typeof onRowClick === 'function') {
      onRowClick(row);
    }
  };

  return (
    <div className="data-table">
      <div className="data-table__scroll">
        <table className="data-table__table">
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column.key} scope="col">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length ? (
              pageRows.map((row, index) => {
                const rowKey = row.id ?? `${page}-${index}`;
                return (
                  <tr
                    key={rowKey}
                    className={classNames('data-table__row', onRowClick && 'data-table__row--clickable')}
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? 'button' : undefined}
                    onClick={onRowClick ? () => handleRowClick(row) : undefined}
                    onKeyDown={event => {
                      if (!onRowClick) {
                        return;
                      }
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleRowClick(row);
                      }
                    }}
                  >
                    {columns.map(column => {
                      const value = row[column.key];
                      const displayValue = column.render ? column.render(value, row) : (value ?? '—');
                      return (
                        <td key={column.key}>{displayValue}</td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="data-table__empty">
                  No hay resultados para mostrar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="data-table__footer">
        <span className="data-table__pagination-info">
          Página {page + 1} de {totalPages}
        </span>
        <div className="data-table__pagination-actions">
          <button
            type="button"
            onClick={handlePrevious}
            className="data-table__pager"
            disabled={page === 0}
            aria-label="Página anterior"
          >
            Anterior
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="data-table__pager"
            disabled={page >= totalPages - 1}
            aria-label="Página siguiente"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}
